// Details tab: survey settings, recipients, and date/time cards. Must live inside the builder's RHF FormProvider.

import React from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    TabPanel,
    Typography,
} from "@material-tailwind/react";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import InfoDialog from "components/dialog/info-dialog";
import YouTubeEmbed from "utils/YouTubeEmbed";
import RHFInput from "components/form/rhf-input";
import RHFTextarea from "components/form/rhf-textarea";
import Image from "components/image";
import PeopleContent from "components/form/people-content";
import UploadPicture from "assets/img/uploadimagepic.png";
import CsvExample from "assets/img/csv-example.png";
import {
    canEditEndDate,
    canEditStartDate,
    canShowInstantActiveCheckbox,
    isEditable,
} from "../lifecycle-visibility";

export default function SettingsPanel({
    survey,
    surveyId,
    instantActive,
    setInstantActive,
    timestamp,
    imageUrl,
    logoDeletable,
    setLogoDeletable,
    inputLogoFileRef,
    handleLogoUploadClick,
    handleDeleteLogo,
    logoUploadHandler,
    surveyRecipients,
    recipientsMutate,
    surveyRecipientsError,
}) {
    const { setValue } = useFormContext();
    const startDateDisabled = !canEditStartDate(survey.state, instantActive);
    const endDateDisabled = !canEditEndDate(survey.state);

    return (
        <TabPanel value="settings" className="grid grid-cols-1 gap-4">
            <Card className="card">
                <CardHeader
                    floated={false}
                    shadow={false}
                    className="card-inner-header pl-0 ml-2 my-2 flex flex-row items-center justify-between"
                >
                    <Typography variant="h3">
                        Survey Details
                    </Typography>
                    <InfoDialog title="Survey Details Help">
                        <div className="text-black">
                            <div>
                                <Typography variant="h2" className="mb-2">
                                    Add survey details
                                </Typography>
                                <YouTubeEmbed
                                    videoId="a7PAZyE8Pxk"
                                    title="Add survey details"
                                />
                            </div>
                            <div className="mt-6">
                                <Typography variant="h2" className="mb-2">
                                    Upload Recipient ID CSV (example)
                                </Typography>
                                <img
                                    src={CsvExample}
                                    alt="CSV example screenshot"
                                    width="100%"
                                    className="rounded-xl my-4"
                                />
                            </div>
                            <div className="mt-6">
                                <Typography variant="h2" className="mb-2">
                                    Create and design survey questions
                                </Typography>
                                <YouTubeEmbed
                                    videoId="L8_Xgg-A6QI"
                                    title="Create and design survey questions"
                                />
                            </div>
                        </div>
                    </InfoDialog>
                </CardHeader>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-2 font-body">
                    {/* Survey Title */}
                    <RHFInput
                        name="name"
                        variant="outlined"
                        type="text"
                        className="text-lg font-display text-primary-text"
                        label="Survey Title (respondents will see this text)"
                        disabled={!isEditable(survey.state)}
                    />

                    {/* Description */}
                    <div className="bg-platinumlight p-4 rounded-xl w-full">
                        <RHFTextarea
                            name="description"
                            className="text-lg font-body text-primary-text"
                            label="Description (for your reference only)"
                            placeholder="This is the page that respondents will see first, before answering the first question, to introduce the survey."
                        />
                    </div>

                    {/* Conditional Fields */}
                    {!!(isEditable(survey.state)) && (
                        <>
                            <div className="bg-platinumlight p-4 rounded-xl w-full">
                                <RHFTextarea
                                    name="start_text"
                                    className="text-lg font-body text-primary-text"
                                    label="Survey Landing Page Text"
                                    placeholder="This is the page that respondents will see first, before answering the first question, to introduce the survey."
                                />
                            </div>
                            <div className="bg-platinumlight p-4 rounded-xl w-full">
                                <RHFTextarea
                                    name="end_text"
                                    className="text-lg font-body text-primary-text"
                                    label="Survey Exit Page Text"
                                    placeholder="This is the page that respondents will see after they finish the last question. You can use this to thank respondents and provide further information on follow up communications."
                                />
                            </div>

                            <div className="bg-platinumlight p-4 rounded-xl">
                                <RHFInput
                                    name="policy_url"
                                    type="text"
                                    className="text-lg font-body text-primary-text"
                                    label="Link to policies (e.g. http://www.example.com)"
                                />
                            </div>

                            {/* Logo Upload */}
                            <div className="bg-platinumlight rounded-xl flex flex-col items-center">
                                <div className="bg-platinumlight">
                                    <Image
                                        alt="survey logo"
                                        key={timestamp}
                                        src={imageUrl}
                                        className="mt-6 rounded-lg border border-black"
                                        onError={({
                                            currentTarget,
                                        }) => {
                                            currentTarget.onerror = null;
                                            currentTarget.src = `${UploadPicture}`;
                                            setLogoDeletable(false);
                                        }}
                                    />
                                </div>
                                <div className="p-2 mb-2">
                                    <Button
                                        variant="filled"
                                        type="button"
                                        onClick={() =>
                                            handleLogoUploadClick()
                                        }
                                        className="btn btn--primary"
                                    >
                                        Upload Logo
                                    </Button>

                                    {!!logoDeletable && (
                                        <Button
                                            variant="filled"
                                            type="button"
                                            onClick={() =>
                                                handleDeleteLogo()
                                            }
                                            className="btn btn--danger"
                                        >
                                            Remove Logo
                                        </Button>
                                    )}

                                    <input
                                        onChange={logoUploadHandler}
                                        ref={inputLogoFileRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </Card>
            <Card className="card">
                <CardHeader
                    floated={false}
                    shadow={false}
                    className="card-inner-header pl-0 ml-2 my-2"
                >
                    <Typography variant="h3">
                        Manage Recipients
                    </Typography>
                </CardHeader>
                <Card className="card shadow-none bg-platinumlight">
                    <CardBody className="p-2">
                        <Typography
                            variant="paragraph"
                            className="font-body text-primary-text text-lg"
                        >
                            A minimum of one survey invitation option must be selected before launching this survey.
                            <br />
                            You can manage survey invitation options and recipients after a survey has been launched by Pausing the survey e.g.
                            <br />
                            Pause the survey &gt; Make Changes &gt; Save &gt; Relaunch.
                        </Typography>
                    </CardBody>
                </Card>
                <PeopleContent
                    survey={survey}
                    surveyId={surveyId}
                    surveyRecipients={surveyRecipients}
                    recipientsMutate={recipientsMutate}
                    surveyRecipientsError={surveyRecipientsError}
                />
            </Card>
            {/* Info Text */}
            <Card className="card">
                <CardHeader
                    floated={false}
                    shadow={false}
                    className="card-inner-header pl-0 ml-2 my-2"
                >
                    <Typography variant="h3">
                        Survey Time & Date
                    </Typography>
                </CardHeader>
                <CardBody className="card-inner-body card-inner">
                    <div className="w-full flex flex-row gap-2 items-center">
                        <div className="flex-auto font-body">
                            <Typography className="text-primary-text text-lg p-2">
                                A
                                <span className="font-bold">
                                    {` start time `}
                                </span>
                                and
                                <span className="font-bold">
                                    {` end time `}
                                </span>
                                must be selected before launching this survey.
                                <br />
                                You can either type in a time and date or use the calendar symbol to select from a calendar.
                                <br />
                                You can change the End Time for a survey after a survey has been launched by Pausing the survey e.g.
                                <br />
                                Pause the survey &gt; Make Changes &gt; Save &gt; Relaunch.
                            </Typography>
                            {/* Start / End Inputs */}
                            <div className="font-body md:grid md:grid-cols-3 justify-items-stretch gap-4">
                                {/* Start Time Input */}
                                <div className="py-4 max-w-xl min-w-md text-lg p-4 rounded-xl mt-2">
                                    <RHFInput
                                        className="font-display text-lg bg-white"
                                        name="start_date"
                                        type="datetime-local"
                                        label="Enter Start Time"
                                        color="black"
                                        disabled={startDateDisabled}
                                    />
                                </div>

                                {/* End Time Input */}
                                <div className="max-w-xl min-w-md text-lg p-4 rounded-xl mt-2">
                                    <RHFInput
                                        className="font-display text-lg bg-white"
                                        name="end_date"
                                        type="datetime-local"
                                        label="Enter End Time"
                                        color="black"
                                        disabled={endDateDisabled}
                                    />
                                </div>

                                {/* Instant Start Checkbox */}
                                {!!(canShowInstantActiveCheckbox(survey.state)) && (
                                    <div className="pr-4 rounded-xl mt-2 min-w-md p-4">
                                        <Checkbox
                                            checked={instantActive}
                                            label="Set 'Start Time' to be from now"
                                            labelProps={{
                                                className: "text-primary font-body",
                                            }}
                                            className="bg-white"
                                            onChange={() => {
                                                if (instantActive) {
                                                    setInstantActive(false);
                                                } else {
                                                    // shouldValidate: the
                                                    // write clears a
                                                    // stale past-date
                                                    // error and
                                                    // recomputes isValid.
                                                    setValue(
                                                        "start_date",
                                                        dayjs().format(
                                                            "YYYY-MM-DDTHH:mm",
                                                        ),
                                                        {
                                                            shouldDirty: true,
                                                            shouldValidate: true,
                                                        },
                                                    );
                                                    setInstantActive(true);
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </TabPanel>
    );
}
